// ── 상황판 메인 훅 — 상태 + API 페치 + 클릭 핸들러 ──────────────────────────
import { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  getKstYmd, getNextBusinessDayKstYmd, getCurrentYearMonthKst,
  toList, normalizeIp, fmtTime,
} from './utils';
import { toDateString, currentYearKST } from '../../../utils/date';

function buildWidgetNeeds(widgetCodes) {
  const set = new Set(widgetCodes || []);
  const tmWidgets = ['month_call_contract', 'today_meeting_map', 'next_business_meetings'];
  const contractWidgets = ['recent_contracts_mobile'];
  return {
    employees: set.has('kpi_employees'),
    externalUsers: set.has('kpi_external_users'),
    salesPeople: set.has('kpi_sales_roster'),
    onlineUsers: set.has('kpi_online_users') || set.has('recent_online'),
    attendance: set.has('attendance_today'),
    tmCalls: tmWidgets.some((code) => set.has(code)),
    leaves: set.has('leave_status'),
    promotionalOrders: false,
    promotionalSteps: false,
    contracts: contractWidgets.some((code) => set.has(code)),
    cardRevenue: set.has('today_card_revenue'),
    settlementNet: set.has('settlement_net'),
    promoProfit: set.has('promotional_profit'),
  };
}

/**
 * 상황판 상태/데이터 훅
 * @param {{ showToast: Function }} options
 * @returns {{ snapshot, loading, lastUpdated, detail, setDetail, fetchSnapshot, handlers }}
 */
export function useSituationBoard({ showToast, widgetCodes = [], enabled = true }) {
  const [loading,     setLoading]     = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [detail,      setDetail]      = useState(null);
  const [holidays,    setHolidays]    = useState(() => new Set());
  const [snapshot,    setSnapshot]    = useState(() => {
    const today = getKstYmd();
    return {
      employees: [], externalUsers: [], salesPeople: [],
      onlineUsers: [], attendanceRows: [], tmCalls: [],
      leaveRows: [], promotionalOrders: [], promoWorkflowSteps: [],
      contracts: [],
      cardRevenue: null, settlementNet: [], promoProfit: null,
      today,
      tomorrow: getNextBusinessDayKstYmd(new Set()),
    };
  });

  /** 모달 열기 헬퍼 */
  function openDetail(title, rows) {
    setDetail({ title, rows, count: rows.length });
  }

  /** 전체 스냅샷 API 페치 */
  async function fetchSnapshot(holidaySet) {
    if (!enabled) return;
    const needs = buildWidgetNeeds(widgetCodes);
    const today    = getKstYmd();
    const tomorrow = getNextBusinessDayKstYmd(holidaySet ?? holidays);
    const { year, month } = getCurrentYearMonthKst();
    setLoading(true);

    // 개별 API 실패가 전체 스냅샷을 중단시키지 않도록 안전 래퍼 적용
    const safe = (p) => p.catch((err) => { console.error('[상황판] API 호출 실패:', err); return { ok: false, data: null }; });

    // 외부직원 조회: 레거시 /api/admin/external-users → 회사별 /api/admin/ext/:code/users 병렬 호출로 전환
    //   - 회사 목록은 권한 기반 필터링된 /api/my/external-companies에서 가져옴
    //   - AdminLayout도 같은 API를 쓰지만 Outlet context로 공유되지 않아 상황판에서 독립 호출
    //   - 접근 권한 없는 회사는 my-companies가 제외하거나 ext 라우트가 403 → safe()가 빈 배열로 처리
    const companiesRes = needs.externalUsers
      ? await safe(apiClient.get('/api/my/external-companies'))
      : { ok: true, data: [] };
    const accessibleCompanies = needs.externalUsers ? toList(companiesRes) : [];

    const fetchIf = (condition, request, fallback = []) => (
      condition ? safe(request()) : Promise.resolve({ ok: true, data: fallback })
    );

    const [
      employeesRes, externalUsersResults, salesRes, onlineRes,
      attendanceRes, tmCallsRes, leavesRes, promoRes, promoStepsRes,
      contractsRes, cardRevenueRes, settlementNetRes, promoProfitRes,
    ] = await Promise.all([
      fetchIf(needs.employees, () => apiClient.get('/api/admin/users?include_resigned=true')),
      // 회사별 외부직원 병렬 조회 → 배열의 배열 (후속 단계에서 평탄화)
      needs.externalUsers
        ? Promise.all(accessibleCompanies.map((c) =>
          safe(apiClient.get(`/api/admin/ext/${c.code}/users?include_resigned=true`))
        ))
        : Promise.resolve([]),
      fetchIf(needs.salesPeople, () => apiClient.get('/api/admin/sales-people?include_resigned=true')),
      fetchIf(needs.onlineUsers, () => apiClient.get('/api/admin/online-users')),
      fetchIf(needs.attendance, () => apiClient.get(`/api/admin/attendance?year=${year}&month=${month}`)),
      fetchIf(needs.tmCalls, () => apiClient.get(`/api/admin/tm/calls?year=${year}&month=${month}`)),
      fetchIf(needs.leaves, () => apiClient.get('/api/admin/leaves')),
      fetchIf(needs.promotionalOrders, () => apiClient.get('/api/admin/promotional-orders')),
      fetchIf(needs.promotionalSteps, () => apiClient.get('/api/admin/workflow-steps/promotional')),
      fetchIf(needs.contracts, () => apiClient.get('/api/admin/contracts')),
      fetchIf(needs.cardRevenue, () => apiClient.get('/api/admin/situation/today-card-summary'), null),
      fetchIf(needs.settlementNet, () => apiClient.get('/api/admin/situation/pine-settlement-net'), { data: [] }),
      fetchIf(needs.promoProfit, () => apiClient.get('/api/admin/promotional-orders/profit-summary'), null),
    ]);

    // 회사별 외부직원 응답을 단일 배열로 평탄화 (기존 toList(externalUsersRes)와 동일 형태)
    const externalUsersFlat = externalUsersResults.flatMap((r) => toList(r));

    // 월 경계 처리: 1~7일이면 전달 콜도, 내일이 다음달이면 다음달 콜도 가져옴
    let extraTmCalls = [];
    const todayDay      = Number(today.slice(8));
    const tomorrowYear  = Number(tomorrow.slice(0, 4));
    const tomorrowMonth = Number(tomorrow.slice(5, 7));
    const adjFetches = [];
    if (needs.tmCalls && todayDay <= 7) {
      const py = month === 1 ? year - 1 : year;
      const pm = month === 1 ? 12 : month - 1;
      adjFetches.push(apiClient.get(`/api/admin/tm/calls?year=${py}&month=${pm}`));
    }
    if (needs.tmCalls && (tomorrowYear !== year || tomorrowMonth !== month)) {
      adjFetches.push(apiClient.get(`/api/admin/tm/calls?year=${tomorrowYear}&month=${tomorrowMonth}`));
    }
    if (adjFetches.length > 0) {
      const adjResults = await Promise.all(adjFetches.map(safe));
      extraTmCalls = adjResults.flatMap((r) => toList(r));
    }
    const baseTmCalls = toList(tmCallsRes);
    const tmCallsById = new Map([...baseTmCalls, ...extraTmCalls].map((c) => [c.id, c]));
    const mergedTmCalls = Array.from(tmCallsById.values());

    setLoading(false);
    // 클라이언트 현재 시각 (UTC Date) 저장 — fmtTime → toKSTTimeString 가 +9h KST 변환.
    //   nowKST() 사용 시 이미 +9h 된 Date 가 또 +9h 처리되어 시간이 18h 어긋남.
    //   eslint-disable-next-line no-restricted-syntax
    setLastUpdated(new Date());
    if (!employeesRes.ok)  showToast('직원 데이터 오류', 'error');
    if (!tmCallsRes.ok)    showToast('콜 데이터 오류', 'error');
    if (!attendanceRes.ok) showToast('출퇴근 데이터 오류', 'error');
    setSnapshot({
      employees:          toList(employeesRes),
      externalUsers:      externalUsersFlat,
      salesPeople:        toList(salesRes),
      onlineUsers:        toList(onlineRes),
      attendanceRows:     toList(attendanceRes),
      tmCalls:            mergedTmCalls,
      leaveRows:          toList(leavesRes),
      promotionalOrders:  toList(promoRes),
      promoWorkflowSteps: toList(promoStepsRes),
      contracts:          toList(contractsRes),
      cardRevenue:   cardRevenueRes?.ok   ? cardRevenueRes.data                   : null,
      settlementNet: settlementNetRes?.ok ? (settlementNetRes.data?.data || [])   : [],
      promoProfit:   promoProfitRes?.ok   ? promoProfitRes.data                   : null,
      today, tomorrow,
    });
  }

  // 초기 로드: 공휴일 → 스냅샷 순으로 fetch
  useEffect(() => {
    if (!enabled) return;
    // currentYearKST: utils/date.js 사용 (new Date 직접 사용 금지)
    const year = currentYearKST();
    async function init() {
      let hSet = new Set();
      try {
        const res = await apiClient.get(`/api/holidays?year=${year}`);
        if (res.ok && Array.isArray(res.data?.holidays)) {
          hSet = new Set(res.data.holidays);
          setHolidays(hSet);
        }
      } catch (_) { /* 공휴일 로드 실패 시 주말만 건너뜀 */ }
      fetchSnapshot(hSet);
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, widgetCodes.join('|')]);

  // ── 클릭 핸들러 ──────────────────────────────────────────────────────────────
  function onClickEmployees() {
    const rows = snapshot.employees
      .filter((u) => u.is_active !== false && !u.resign_date)
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ko-KR'))
      .map((u) => ({ primary: u.name || '-', secondary: `${u.department || '부서없음'} · ${u.worker_type || '-'}`, right: u.hire_date ? u.hire_date.slice(0, 10) : '-', rightSub: '입사일' }));
    openDetail('재직 직원', rows);
  }
  function onClickExternal() {
    const rows = snapshot.externalUsers
      .filter((u) => u.is_active !== false && !u.resign_date)
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ko-KR'))
      .map((u) => ({ primary: u.name || '-', secondary: u.department || '부서없음', right: u.hire_date ? u.hire_date.slice(0, 10) : '-', rightSub: '입사일' }));
    openDetail('외부직원', rows);
  }
  function onClickSales() {
    const rows = snapshot.salesPeople
      .filter((u) => u.is_active !== false && !u.resign_date)
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ko-KR'))
      .map((u) => ({ primary: u.name || '-', secondary: u.department || '-', right: u.phone || '-' }));
    openDetail('마케팅 명부', rows);
  }
  function onClickOnline() {
    const rows = (snapshot.onlineUsers || []).map((u) => ({
      primary: u.name || '-', secondary: normalizeIp(u.ip_address) || '-',
      right: fmtTime(u.last_seen_at), rightSub: '최근 활동',
    }));
    openDetail('현재 접속', rows);
  }
  function onClickCheckedIn() {
    const today = snapshot.today;
    const rows = (snapshot.attendanceRows || [])
      .filter((r) => toDateString(r.date || '') === today && r.check_in)
      .sort((a, b) => String(a.check_in).localeCompare(String(b.check_in)))
      .map((r) => ({ primary: r.name || '-', right: fmtTime(r.check_in), rightSub: '출근시간' }));
    openDetail('오늘 출근', rows);
  }
  function onClickCheckedOut() {
    const today = snapshot.today;
    const rows = (snapshot.attendanceRows || [])
      .filter((r) => toDateString(r.date || '') === today && r.check_out)
      .sort((a, b) => String(a.check_out).localeCompare(String(b.check_out)))
      .map((r) => ({ primary: r.name || '-', secondary: `출근 ${fmtTime(r.check_in)}`, right: fmtTime(r.check_out), rightSub: '퇴근시간' }));
    openDetail('오늘 퇴근', rows);
  }
  function onClickNotOut() {
    const today = snapshot.today;
    const rows = (snapshot.attendanceRows || [])
      .filter((r) => toDateString(r.date || '') === today && r.check_in && !r.check_out)
      .sort((a, b) => String(a.check_in).localeCompare(String(b.check_in)))
      .map((r) => ({ primary: r.name || '-', secondary: `출근 ${fmtTime(r.check_in)}`, right: '미퇴근' }));
    openDetail('미퇴근', rows);
  }
  function onClickMonthCalls() {
    const byUser = {};
    (snapshot.tmCalls || []).forEach((r) => {
      const name = r.user_name || '-';
      if (!byUser[name]) byUser[name] = 0;
      byUser[name] += 1;
    });
    const rows = Object.entries(byUser).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ primary: name, right: `${count}건` }));
    openDetail('이번달 콜 현황', rows);
  }
  function onClickConversionRate() {
    const byUser = {};
    (snapshot.tmCalls || []).forEach((r) => {
      const name = r.user_name || '-';
      if (!byUser[name]) byUser[name] = { calls: 0, contracts: 0 };
      byUser[name].calls += 1;
      if (r.is_contracted === true || r.contract_status === 'normal') byUser[name].contracts += 1;
    });
    const rows = Object.entries(byUser)
      .map(([name, { calls, contracts }]) => {
        const rate = calls > 0 ? Math.round((contracts / calls) * 100) : 0;
        return { name, calls, contracts, rate };
      })
      .sort((a, b) => b.rate - a.rate || b.contracts - a.contracts)
      .map(({ name, calls, contracts, rate }) => ({
        primary: name, secondary: `콜 ${calls}건  ·  계약 ${contracts}건`,
        right: `${rate}%`, rightSub: '계약율',
      }));
    setDetail({ title: '이번달 계약율 (직원별)', rows, count: rows.length, countSuffix: '명' });
  }
  function onClickMonthContracts() {
    const rows = (snapshot.tmCalls || [])
      .filter((r) => r.is_contracted === true || r.contract_status === 'normal')
      .sort((a, b) => String(b.contract_date || b.visit_date || '').localeCompare(String(a.contract_date || a.visit_date || '')))
      .map((r) => {
        /* contract_items 배열 기반으로 계약 상품 표시 */
        const items    = Array.isArray(r.contract_items) ? r.contract_items : [];
        const normal   = items.filter((i) => !i.isService);
        const svcItems = items.filter((i) => i.isService);
        const product  = normal.length ? normal.map((i) => `${i.ticketTypeName || '?'}×${i.quantity}`).join(' · ') : '-';
        const svcCount = svcItems.length ? `서비스 ${svcItems.map((i) => `${i.ticketTypeName || '?'}×${i.quantity}`).join('/')}` : null;
        const detail   = [product, svcCount].filter(Boolean).join('  ·  ');
        return { primary: r.company_name || '-', secondary: [r.user_name, r.sales_person_name].filter(Boolean).join(' · ') || '-', detail, right: (r.contract_date || r.visit_date || '').slice(0, 10) || '-', rightSub: '계약일' };
      });
    openDetail('이번달 계약', rows);
  }
  function onClickPending() {
    const rows = (snapshot.tmCalls || [])
      .filter((r) => r.contract_status === 'pending')
      .sort((a, b) => String(b.visit_date || '').localeCompare(String(a.visit_date || '')))
      .map((r) => ({ primary: r.company_name || '-', secondary: r.user_name || '-', right: (r.visit_date || '').slice(0, 10) || '-', rightSub: '방문일' }));
    openDetail('계약 검토 대기', rows);
  }
  function onClickPromoStep(stepName, orders) {
    const rows = [...orders]
      .sort((a, b) => String(b.received_date || '').localeCompare(String(a.received_date || '')))
      .map((r) => ({ primary: r.company_name || r.sales_person_name || '-', secondary: r.category_name || '-', right: (r.received_date || '').slice(0, 10) || '-', rightSub: '접수일' }));
    openDetail(`이번달 홍보물 — ${stepName}`, rows);
  }
  // ── 오늘 카드결제 상세 (전화승인 + 카드승인, 필터 지원) ──────────────────────
  // typeFilter: null=전체, '전화승인', '카드승인'
  async function onClickCardRevenue(typeFilter) {
    const titleMap = { '전화승인': '오늘 전화승인 상세', '카드승인': '오늘 카드승인 상세' };
    const title = titleMap[typeFilter] || '오늘 카드결제 상세';
    try {
      const res = await apiClient.get('/api/admin/situation/today-card-detail');
      let data = res.ok ? (res.data?.data || []) : [];
      // 타입 필터 적용
      if (typeFilter) data = data.filter((r) => r.type === typeFilter);
      const rows = data.map((r) => ({
        primary: r.card_company || '-',
        // 전화승인: 입력자 표시, 카드승인: 가맹점 표시
        secondary: r.type === '카드승인'
          ? `가맹점 · ${r.user_name || '-'}`
          : `입력자 · ${r.user_name || '-'}`,
        right: `${Number(r.amount || 0).toLocaleString()}원`,
        rightSub: '금액',
      }));
      openDetail(title, rows);
    } catch { openDetail(title, []); }
  }

  return {
    snapshot, loading, lastUpdated,
    detail, setDetail,
    fetchSnapshot,
    handlers: {
      onClickEmployees, onClickExternal, onClickSales, onClickOnline,
      onClickCheckedIn, onClickCheckedOut, onClickNotOut,
      onClickMonthCalls, onClickConversionRate, onClickMonthContracts,
      onClickPending, onClickPromoStep, onClickCardRevenue,
    },
  };
}
